/**
 * Geode Voice (UPT-2000 V1 §Arborescence, UPT-2124 J2a spec `web/SPEC-UPT-2124-J2.md` §2 n° 3) — registre des outils vocaux enregistrés par une page
 * ou un module : `core/components/voice` ne connaît pas Module Studio ; une page ENREGISTRE un outil à son montage et le RETIRE à son démontage.
 * Le registre est le pont générique ; `next_step` (la fiche d'un dossier, « Géode, que dois-je faire ? ») en est le premier outil.
 *
 * - `register(tool)` rend la fonction de retrait ; un second enregistrement du même nom remplace le premier.
 * - `list()` : les définitions (`VoiceToolDefinition`), envoyées au modèle vocal par `session.update` à l'ouverture du canal de données.
 * - `execute(name, args)` : exécute l'outil enregistré, ou rend `null` quand aucun outil ne porte ce nom (`executeVoiceTool` replie alors sur `unknown_tool`).
 * - `act(name, action, card)` : un geste d'une carte produite par l'outil (ex. « Ouvrir », « Voir le détail »), joué par la page qui l'a enregistré.
 * - `subscribe(listener)` : les composants (dock, panneau) suivent la présence d'un outil (`useSyncExternalStore`).
 *
 * Pur : aucune lecture réseau, aucune écriture ; mémoire seule (jamais de stockage).
 */
import type { VoiceCard, VoiceToolDefinition } from './voiceTypes';

/** Sortie d'un outil : le JSON renvoyé au modèle (même forme que `executeVoiceTool`). */
export type VoiceToolOutput = { outputJson: string };

export interface VoiceRegisteredTool {
  definition: VoiceToolDefinition;
  /** Exécution côté navigateur, avec les arguments déjà analysés. Aucune écriture. */
  execute: (args: Record<string, unknown>) => Promise<VoiceToolOutput> | VoiceToolOutput;
  /** Gestes des cartes produites par l'outil (facultatif) : `action` est un nom libre (`open`, `detail`…). */
  act?: (action: string, card: VoiceCard) => void;
}

type Listener = () => void;

const tools = new Map<string, VoiceRegisteredTool>();
const listeners = new Set<Listener>();
/** Instantané des définitions, stable tant que le registre ne change pas (exigence de `useSyncExternalStore`). */
let snapshot: VoiceToolDefinition[] = [];

const notify = (): void => {
  snapshot = Array.from(tools.values()).map((tool) => tool.definition);
  listeners.forEach((listener) => listener());
};

/** Enregistre un outil ; rend la fonction qui le retire (seulement s'il est encore celui-là : un remplacement n'est pas retiré par l'ancien). */
export const registerVoiceTool = (tool: VoiceRegisteredTool): (() => void) => {
  const name = tool.definition.name;
  tools.set(name, tool);
  notify();
  return () => {
    if (tools.get(name) === tool) {
      tools.delete(name);
      notify();
    }
  };
};

export const unregisterVoiceTool = (name: string): void => {
  if (tools.delete(name)) {
    notify();
  }
};

export const hasVoiceTool = (name: string): boolean => tools.has(name);

/** Les définitions des outils enregistrés (instantané stable). */
export const listVoiceTools = (): VoiceToolDefinition[] => snapshot;

/** Exécute l'outil enregistré sous ce nom ; `null` quand aucun ne le porte. */
export const executeRegisteredVoiceTool = async (name: string, args: Record<string, unknown>): Promise<VoiceToolOutput | null> => {
  const tool = tools.get(name);
  if (!tool) {
    return null;
  }
  return tool.execute(args);
};

/** Joue un geste d'une carte de l'outil ; vrai quand l'outil est enregistré et sait le jouer. */
export const actOnVoiceCard = (name: string, action: string, card: VoiceCard): boolean => {
  const tool = tools.get(name);
  if (!tool?.act) {
    return false;
  }
  tool.act(action, card);
  return true;
};

export const subscribeVoiceTools = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Vide le registre (tests). */
export const clearVoiceTools = (): void => {
  tools.clear();
  notify();
};
